import { useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';

import { syncTimezone } from '../callables';
import { useAuth } from '../firebase/FirebaseContext';

/**
 * App-level timezone sync. Mounted once near the root (sibling to
 * `useGaUserIdentity`). Reports the device's IANA timezone
 * (Intl.DateTimeFormat().resolvedOptions().timeZone) to the
 * `theaWebSyncTimezone` callable, which stores it on the theaWebUser
 * doc. First consumer: the day-of birthday email — the nightly job
 * uses the tz to queue the Klaviyo event so the flow releases it on
 * the user's LOCAL birthday morning (thea-serverless #73).
 *
 * Fires per (uid, timezone) pair, guarded by localStorage — so it
 * re-syncs after a uid change (anon → permanent merge), a device
 * timezone change (travel), or a first visit, and stays silent on
 * every other load. Telemetry-grade: failures are swallowed and
 * retried naturally on the next visit.
 */

const STORAGE_KEY = 'thea:tz_synced';

export function useSyncTimezone(): void {
  const auth = useAuth();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) return;

      let tz: string | undefined;
      try {
        tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch {
        return; // ancient browser — nothing to report
      }
      if (!tz) return;

      const stamp = `${user.uid}:${tz}`;
      try {
        if (window.localStorage.getItem(STORAGE_KEY) === stamp) return;
      } catch {
        /* localStorage unavailable (private mode) — sync every load; harmless */
      }

      syncTimezone({ timezone: tz })
        .then((res) => {
          if (res.data.success) {
            try {
              window.localStorage.setItem(STORAGE_KEY, stamp);
            } catch {
              /* best-effort guard only */
            }
          }
        })
        .catch(() => {
          /* telemetry-grade write — retry on next visit */
        });
    });
    return () => unsub();
  }, [auth]);
}

export default useSyncTimezone;
